// src/pages/admin-audit-management/components/UserAssignmentPanel.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import AssignmentConfirmationModal from './AssignmentConfirmationModal';
import toast from 'react-hot-toast';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalizeFile = (file) => {
  const storagePath = file.full_path || file.storagePath || file.html_storage_path;
  const fileName    = storagePath?.split('/').pop();

  const rawType = (
    file.report_type        ||
    file.reportType         ||
    file.auditType          ||
    file.json_data?.report_type ||
    ''
  ).toString().toUpperCase().trim();

  const auditType = rawType === 'PAID' ? 'paid' : 'free';

  return {
    ...file,
    id:          file.id || file.report_id || storagePath || fileName,
    storagePath, full_path: storagePath,
    name:        file.name || fileName,
    file_name:   file.file_name || fileName,
    arrivedAt:   file.arrivedAt || file.generatedAt || new Date().toISOString(),
    auditType,
    report_type: rawType || 'FREE',
  };
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return 'just now';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric' });
};

const shortenFileName = (name = '') => {
  const base = name.replace('.html', '');
  if (base.length <= 32) return name;
  return base.slice(0, 16) + '…' + base.slice(-10) + '.html';
};

const getInitials = (name = '') =>
  name.trim().split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';

const AVATAR_PALETTE = ['#0ea5e9','#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6'];
const avatarBg = (name = '') => AVATAR_PALETTE[(name.charCodeAt(0) || 0) % AVATAR_PALETTE.length];

// ─── CSS injected once ────────────────────────────────────────────────────────
const injectStyles = () => {
  if (document.getElementById('uap-styles')) return;
  const s = document.createElement('style');
  s.id = 'uap-styles';
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@500;700&display=swap');

    .uap-root * { box-sizing: border-box; }
    .uap-root { font-family: 'DM Sans', -apple-system, sans-serif; }

    .uap-panel {
      background: #fff;
      border-radius: 16px;
      border: 1px solid #e8ecf0;
      box-shadow: 0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04);
      overflow: hidden;
    }

    .uap-header {
      display: flex; align-items: center; justify-content: space-between;
      padding: 16px 20px;
      background: #fafbfc;
      border-bottom: 1px solid #e8ecf0;
    }
    .uap-header-icon {
      width: 36px; height: 36px; border-radius: 10px;
      background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 2px 8px rgba(0,0,0,0.18);
      flex-shrink: 0;
    }
    .uap-title { font-size: 15px; font-weight: 700; color: #0d0d12; letter-spacing: -0.02em; }
    .uap-subtitle { font-size: 12px; color: #8b93a1; font-weight: 400; margin-top: 1px; }

    .uap-pill {
      display: inline-flex; align-items: center; gap: 5px;
      font-size: 11px; font-weight: 600; letter-spacing: 0.01em;
      padding: 3px 9px; border-radius: 100px;
    }
    .uap-pill-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
    .uap-pill-amber  { background: #fef9ec; color: #92620a; border: 1px solid #fde68a; }
    .uap-pill-indigo { background: #eef2ff; color: #3730a3; border: 1px solid #c7d2fe; }
    .uap-pill-green  { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }

    .uap-col-header {
      display: flex; align-items: center;
      padding: 0 20px; height: 32px;
      background: #f4f6f8;
      border-bottom: 1px solid #e8ecf0;
    }
    .uap-col-label {
      font-size: 10px; font-weight: 700; color: #9aa3af;
      text-transform: uppercase; letter-spacing: 0.08em;
    }

    .uap-file-row {
      display: flex; align-items: center; gap: 14px;
      padding: 0 20px 0 0;
      min-height: 70px;
      border-bottom: 1px solid #f0f2f5;
      transition: background 0.12s ease;
      cursor: default;
      position: relative;
    }
    .uap-file-row:hover { background: #f8f9fb; }
    .uap-file-row:last-child { border-bottom: none; }
    .uap-file-row-accent {
      width: 3px; align-self: stretch; flex-shrink: 0; border-radius: 0 2px 2px 0;
    }
    .uap-file-icon {
      width: 38px; height: 38px; border-radius: 10px;
      display: flex; align-items: center; justify-content: center; flex-shrink: 0;
      border: 1px solid #e8ecf0;
    }

    .uap-badge {
      display: inline-flex; align-items: center; gap: 4px;
      font-size: 10px; font-weight: 700; letter-spacing: 0.03em;
      padding: 3px 8px; border-radius: 6px;
      text-transform: uppercase; flex-shrink: 0;
    }
    .uap-badge-paid {
      background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
      color: #065f46; border: 1px solid #6ee7b7;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.6);
    }
    .uap-badge-free    { background: #f1f5f9; color: #94a3b8; border: 1px solid #e2e8f0; }
    .uap-badge-pending { background: #fef9ec; color: #b45309; border: 1px solid #fde68a; }
    .uap-badge-queued  { background: #eef2ff; color: #4338ca; border: 1px solid #c7d2fe; }
    .uap-badge-completed {
      background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0;
    }
    .uap-badge-delivered { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }

    .uap-filename {
      font-family: 'JetBrains Mono', monospace;
      font-size: 12.5px; font-weight: 600; color: #1a1f2e;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      letter-spacing: -0.03em; line-height: 1.4;
    }

    .uap-assign-btn {
      display: inline-flex; align-items: center; gap: 6px;
      height: 34px; padding: 0 14px; border-radius: 8px;
      font-size: 12px; font-weight: 600;
      cursor: pointer; outline: none; flex-shrink: 0;
      transition: all 0.15s ease;
      border: 1px solid #e2e8f0;
      background: #fff; color: #475569;
      box-shadow: 0 1px 2px rgba(0,0,0,0.05);
      font-family: 'DM Sans', sans-serif;
    }
    .uap-assign-btn:hover {
      border-color: #6366f1; background: #fafbff; color: #4338ca;
      box-shadow: 0 0 0 3px rgba(99,102,241,0.08), 0 1px 2px rgba(0,0,0,0.05);
    }
    .uap-assign-btn-active {
      border: 1px solid #c7d2fe; background: #eef2ff; color: #3730a3;
      box-shadow: 0 0 0 3px rgba(99,102,241,0.08);
    }

    .uap-delivered-chip {
      display: inline-flex; align-items: center; gap: 6px;
      height: 28px; padding: 0 10px 0 7px; border-radius: 100px;
      background: #f0fdf4; border: 1.5px solid #86efac;
      font-size: 12px; font-weight: 700; color: #166534;
    }

    .uap-action-btn {
      width: 30px; height: 30px; border-radius: 8px;
      border: 1px solid transparent; background: transparent;
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; transition: all 0.12s ease;
      color: #94a3b8; flex-shrink: 0;
    }
    .uap-action-btn:hover.preview  { background: #f1f5f9; border-color: #e2e8f0; color: #334155; }
    .uap-action-btn:hover.download { background: #eff6ff; border-color: #bfdbfe; color: #2563eb; }
    .uap-action-btn:hover.delete   { background: #fef2f2; border-color: #fecaca; color: #dc2626; }

    .uap-dl-btn {
      display: flex; align-items: center; gap: 6px;
      height: 34px; padding: 0 14px; border-radius: 9px;
      border: 1px solid #e2e8f0; background: #fff;
      font-size: 12px; font-weight: 600; color: #334155;
      cursor: pointer; transition: all 0.1s;
    }
    .uap-dl-btn:hover { background: #f8f9fb; border-color: #d1d5db; }

    .uap-assign-all-btn {
      display: flex; align-items: center; gap: 6px;
      height: 34px; padding: 0 16px; border-radius: 9px;
      border: none; background: #0d0d12;
      font-size: 12px; font-weight: 700; color: #fff;
      cursor: pointer; transition: all 0.12s;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.12);
    }
    .uap-assign-all-btn:hover { background: #1a1f2e; transform: translateY(-1px); box-shadow: 0 2px 6px rgba(0,0,0,0.2), 0 8px 20px rgba(0,0,0,0.14); }
    .uap-assign-all-btn:active { transform: translateY(0); }

    .uap-empty {
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      padding: 56px 0; gap: 10px;
    }
    .uap-empty-icon {
      width: 48px; height: 48px; border-radius: 14px;
      background: #f4f6f8; border: 1px solid #e8ecf0;
      display: flex; align-items: center; justify-content: center;
    }

    .uap-dropdown {
      position: absolute; z-index: 99999;
      background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.04), 0 20px 40px rgba(0,0,0,0.10);
      overflow: hidden;
      font-family: 'DM Sans', -apple-system, sans-serif;
    }
    .uap-dropdown-search {
      padding: 8px; background: #fafbfc; border-bottom: 1px solid #f0f2f5;
    }
    .uap-dropdown-input-wrap {
      display: flex; align-items: center; gap: 8px;
      height: 34px; padding: 0 10px;
      background: #fff; border: 1px solid #e2e8f0; border-radius: 8px;
    }
    .uap-dropdown-input {
      flex: 1; font-size: 13px; outline: none; border: none;
      background: transparent; color: #0d0d12;
      font-family: 'DM Sans', sans-serif;
    }
    .uap-dropdown-row {
      width: 100%; display: flex; align-items: center; gap: 10px;
      padding: 0 12px; height: 48px; text-align: left;
      border: none; border-bottom: 1px solid #f8f9fb;
      cursor: pointer; transition: background 0.08s;
      font-family: 'DM Sans', sans-serif;
    }
    .uap-dropdown-row:last-child { border-bottom: none; }
    .uap-dropdown-row:hover { background: #f8f9fb; }
    .uap-dropdown-row.selected { background: #f0f1ff; }
    .uap-dropdown-footer {
      display: flex; gap: 12px; padding: 7px 12px;
      border-top: 1px solid #f0f2f5; background: #fafbfc;
    }
    .uap-legend { display: flex; align-items: center; gap: 5px; font-size: 11px; color: #9aa3af; }

    .uap-collapse-btn {
      width: 100%; display: flex; align-items: center; gap: 8px;
      padding: 10px 20px; background: transparent; border: none;
      cursor: pointer; text-align: left; transition: background 0.1s;
    }
    .uap-collapse-btn:hover { background: #f8f9fb; }

    .uap-done-banner {
      display: flex; align-items: center; gap: 10px;
      padding: 12px 20px; background: #f0fdf4; border-top: 1px solid #bbf7d0;
    }

    .uap-warn {
      display: flex; align-items: center; gap: 10px;
      padding: 10px 20px; background: #fffbeb; border-bottom: 1px solid #fde68a;
    }

    .uap-backdrop {
      position: fixed; inset: 0; z-index: 9998;
      background: rgba(13,13,18,0.6); backdrop-filter: blur(6px);
    }
    .uap-preview-shell {
      position: fixed; top: 16px; left: 50%; transform: translateX(-50%);
      z-index: 9999; width: 96vw; height: calc(100vh - 32px);
      background: #fff; border-radius: 16px; overflow: hidden;
      box-shadow: 0 24px 64px rgba(0,0,0,0.24);
      display: flex; flex-direction: column;
    }
    .uap-preview-bar {
      display: flex; align-items: center; justify-content: space-between;
      padding: 0 16px; height: 52px; border-bottom: 1px solid #f0f2f5; flex-shrink: 0;
    }
    .uap-delete-modal {
      position: fixed; inset: 0; z-index: 9999;
      display: flex; align-items: center; justify-content: center; padding: 16px;
    }
    .uap-delete-card {
      background: #fff; border-radius: 14px; width: 100%; max-width: 380px;
      box-shadow: 0 20px 50px rgba(0,0,0,0.15); border: 1px solid #e8ecf0; overflow: hidden;
    }

    .uap-time-val { font-size: 12px; font-weight: 600; color: #334155; font-variant-numeric: tabular-nums; }
    .uap-time-sub { font-size: 10px; color: #9aa3af; margin-top: 1px; }

    /* ── Mismatch modal — 7-fix MNC standard ── */
    .uap-mm-backdrop {
      position: fixed; inset: 0; z-index: 99999;
      display: flex; align-items: center; justify-content: center; padding: 20px;
      background: rgba(0,0,0,0.72);
      backdrop-filter: blur(18px); -webkit-backdrop-filter: blur(18px);
    }
    .uap-mm-card {
      width: 100%; max-width: 392px;
      background: #111113;
      border-radius: 16px;
      border: 1px solid #1c1c1f;
      overflow: hidden;
      box-shadow: 0 0 0 1px rgba(255,255,255,0.03), 0 32px 64px rgba(0,0,0,0.6);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    }
    /* FIX 1+2 — compact header, icon inline at 32px */
    .uap-mm-header {
      display: flex; align-items: center; gap: 12px;
      padding: 18px 20px 16px;
      border-bottom: 1px solid #1a1a1d;
    }
    .uap-mm-icon {
      width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
      background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.18);
    }
    .uap-mm-title { font-size: 14px; font-weight: 600; color: #f0f0f0; letter-spacing: -0.015em; margin-bottom: 2px; }
    .uap-mm-subtitle { font-size: 11.5px; color: #52525b; line-height: 1.4; }
    /* Body */
    .uap-mm-body { padding: 16px 20px; }
    /* FIX 3 — 2-col compare, no dead center column */
    .uap-mm-compare {
      display: grid; grid-template-columns: 1fr 1fr;
      border: 1px solid #1c1c1f; border-radius: 10px; overflow: hidden;
      margin-bottom: 12px;
    }
    .uap-mm-col { padding: 12px 13px; }
    .uap-mm-col:first-child { border-right: 1px solid #1a1a1d; }
    .uap-mm-col-label {
      font-size: 9.5px; font-weight: 600; letter-spacing: 0.09em; text-transform: uppercase;
      color: #3f3f46; margin-bottom: 9px;
    }
    /* FIX 7 — avatar before name */
    .uap-mm-avatar-row { display: flex; align-items: center; gap: 7px; margin-bottom: 8px; }
    .uap-mm-avatar {
      width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
      font-size: 9.5px; font-weight: 700; color: #fff;
    }
    .uap-mm-name { font-size: 12.5px; font-weight: 600; color: #e4e4e7; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .uap-mm-filename {
      font-size: 10.5px; color: #3f3f46; margin-bottom: 8px;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      font-family: 'JetBrains Mono', 'SF Mono', monospace;
    }
    /* Badges */
    .uap-mm-badge {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 3px 9px; border-radius: 6px;
      font-size: 10.5px; font-weight: 600; letter-spacing: 0.01em;
    }
    .uap-mm-badge-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
    .uap-mm-badge-paid { background: rgba(16,185,129,0.1); color: #34d399; border: 1px solid rgba(16,185,129,0.22); }
    .uap-mm-badge-paid .uap-mm-badge-dot { background: #10b981; }
    .uap-mm-badge-free { background: rgba(245,158,11,0.1); color: #fbbf24; border: 1px solid rgba(245,158,11,0.22); }
    .uap-mm-badge-free .uap-mm-badge-dot { background: #f59e0b; }
    /* FIX 3 — conflict pill */
    .uap-mm-conflict {
      display: flex; align-items: center; gap: 7px;
      padding: 7px 10px; background: rgba(239,68,68,0.05);
      border: 1px solid rgba(239,68,68,0.12); border-radius: 7px;
      margin-bottom: 12px;
    }
    .uap-mm-conflict-dot { width: 5px; height: 5px; border-radius: 50%; background: #ef4444; flex-shrink: 0; }
    .uap-mm-conflict-text { font-size: 11px; color: #6b2d2d; }
    /* FIX 4 — inline warning text, no box */
    .uap-mm-warn-text { font-size: 12px; color: #6b4a08; line-height: 1.65; }
    .uap-mm-warn-text strong { color: #b07d18; font-weight: 600; }
    /* Footer */
    .uap-mm-footer {
      display: flex; gap: 8px;
      padding: 14px 20px 18px;
      border-top: 1px solid #1a1a1d;
    }
    .uap-mm-cancel {
      flex: 1; height: 36px; border-radius: 8px; cursor: pointer;
      font-size: 12.5px; font-weight: 500; color: #52525b;
      background: transparent; border: 1px solid #1e1e21;
      font-family: inherit; display: flex; align-items: center; justify-content: center; gap: 6px;
      transition: all 0.14s; letter-spacing: -0.01em;
    }
    .uap-mm-cancel:hover { background: #1a1a1d; color: #71717a; }
    /* FIX 5 — destructive btn label with consequence text */
    .uap-mm-override {
      flex: 1.6; height: 36px; border-radius: 8px; cursor: pointer;
      font-size: 12px; font-weight: 600; color: #ef4444;
      background: transparent; border: 1px solid rgba(239,68,68,0.18);
      font-family: inherit; display: flex; align-items: center; justify-content: center; gap: 6px;
      transition: all 0.14s; letter-spacing: -0.01em; white-space: nowrap;
    }
    .uap-mm-override:hover { background: rgba(239,68,68,0.08); border-color: rgba(239,68,68,0.3); }

    @keyframes uap-fadein { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
    .uap-fadein { animation: uap-fadein 0.2s ease-out; }

    @keyframes uap-shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-6px)} 40%{transform:translateX(6px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }
    .uap-shake { animation: uap-shake 0.4s ease-out; }
  `;
  document.head.appendChild(s);
};

// ─── Audit Type Mismatch Modal ────────────────────────────────────────────────

const AuditTypeMismatchModal = ({ isOpen, onClose, onOverride, fileAuditType, userAuditType, fileName, userName }) => {
  if (!isOpen) return null;

  const expected       = userAuditType?.toLowerCase() === 'paid' ? 'paid' : 'free';
  const uploaded       = fileAuditType?.toLowerCase()  === 'paid' ? 'paid' : 'free';
  const isPaidExpected = expected === 'paid';

  const initials  = getInitials(userName);
  const avatarColor = avatarBg(userName || '');

  const shortFile = fileName
    ? fileName.replace('.html', '').slice(0, 24) + (fileName.length > 24 ? '…' : '')
    : 'Report';

  const conflictLabel = isPaidExpected
    ? 'Type conflict — Paid requested, Free uploaded'
    : 'Type conflict — Free requested, Paid uploaded';

  const warnCopy = isPaidExpected
    ? <p className="uap-mm-warn-text">Assigning this will omit <strong>competitor benchmarks, battle cards,</strong> and outreach sequences — content this customer paid for.</p>
    : <p className="uap-mm-warn-text">Assigning this exposes <strong>paid-tier content</strong> to a customer on a free request. Check with your team before proceeding.</p>;

  return createPortal(
    <div className="uap-mm-backdrop uap-fadein" onClick={onClose}>
      <div className="uap-mm-card uap-shake" onClick={e => e.stopPropagation()}>

        {/* FIX 1 + 2 — icon inline with title at 32px, title at 14px */}
        <div className="uap-mm-header">
          <div className="uap-mm-icon">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <div>
            <div className="uap-mm-title">Audit type mismatch</div>
            <div className="uap-mm-subtitle">Report doesn't match this customer's request</div>
          </div>
        </div>

        <div className="uap-mm-body">

          {/* FIX 3 — 2-col compare, no dead center column */}
          <div className="uap-mm-compare">

            {/* Left — customer with avatar (FIX 7) */}
            <div className="uap-mm-col">
              <div className="uap-mm-col-label">Requested by</div>
              <div className="uap-mm-avatar-row">
                <div className="uap-mm-avatar" style={{ background: avatarColor }}>{initials}</div>
                <div className="uap-mm-name">{userName || 'User'}</div>
              </div>
              <span className={`uap-mm-badge uap-mm-badge-${expected}`}>
                <span className="uap-mm-badge-dot" />
                {expected === 'paid' ? 'Paid audit' : 'Free audit'}
              </span>
            </div>

            {/* Right — uploaded file */}
            <div className="uap-mm-col" style={{ textAlign: 'right' }}>
              <div className="uap-mm-col-label">File uploaded</div>
              <div className="uap-mm-filename" style={{ marginTop: 8 }}>{shortFile}</div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <span className={`uap-mm-badge uap-mm-badge-${uploaded}`}>
                  <span className="uap-mm-badge-dot" />
                  {uploaded === 'paid' ? 'Paid audit' : 'Free audit'}
                </span>
              </div>
            </div>

          </div>

          {/* FIX 3 — conflict pill instead of dead ✕ column */}
          <div className="uap-mm-conflict">
            <div className="uap-mm-conflict-dot" />
            <div className="uap-mm-conflict-text">{conflictLabel}</div>
          </div>

          {/* FIX 4 — plain inline text, no nested warning box */}
          {warnCopy}

        </div>

        {/* Footer */}
        <div className="uap-mm-footer">
          <button className="uap-mm-cancel" onClick={onClose}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
            Cancel
          </button>
          {/* FIX 5 — consequence in button label */}
          <button className="uap-mm-override" onClick={onOverride}>
            Assign anyway — I understand
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
};

// ─── Assign Dropdown ──────────────────────────────────────────────────────────

const AssignSelect = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const selected = options.find(o => o.value === value);
  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()) || (o.reqId || '').toLowerCase().includes(search.toLowerCase()))
    : options;

  const calcPos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const dropH = Math.min(filtered.length * 48 + 100, 310);
    const below = window.innerHeight - r.bottom;
    const up = below < dropH && r.top > below;
    setPos({
      top: up ? r.top + window.scrollY - dropH - 6 : r.bottom + window.scrollY + 6,
      left: r.right + window.scrollX - Math.max(r.width, 280),
      width: Math.max(r.width, 280),
    });
  }, [filtered.length]);

  useEffect(() => { if (open) { calcPos(); setTimeout(() => inputRef.current?.focus(), 40); } }, [open, calcPos]);
  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', calcPos, true);
    window.addEventListener('resize', calcPos);
    return () => { window.removeEventListener('scroll', calcPos, true); window.removeEventListener('resize', calcPos); };
  }, [open, calcPos]);
  useEffect(() => {
    if (!open) return;
    const fn = (e) => {
      if (!triggerRef.current?.contains(e.target) && !panelRef.current?.contains(e.target)) {
        setOpen(false); setSearch('');
      }
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  const pick = (opt) => { onChange(opt.value); setOpen(false); setSearch(''); };
  const clear = (e) => { e.stopPropagation(); onChange(''); };

  return (
    <>
      <button
        ref={triggerRef} type="button"
        onClick={() => setOpen(v => !v)}
        className={`uap-assign-btn ${selected ? 'uap-assign-btn-active' : ''}`}
      >
        {selected ? (
          <>
            <span style={{ width: 18, height: 18, borderRadius: '50%', background: avatarBg(selected.label), color: '#fff', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {getInitials(selected.label)}
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#3730a3', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selected.label.split(' ')[0]}
            </span>
            <span onClick={clear} style={{ width: 14, height: 14, borderRadius: '50%', background: '#c7d2fe', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, color: '#3730a3' }}>
              <Icon name="X" size={8} />
            </span>
          </>
        ) : (
          <>
            <Icon name="UserPlus" size={12} />
            <span>Assign</span>
            <Icon name="ChevronDown" size={11} style={{ color: '#94a3b8' }} />
          </>
        )}
      </button>

      {open && createPortal(
        <div ref={panelRef} className="uap-dropdown uap-fadein" style={{ top: pos.top, left: pos.left, width: pos.width }}>
          <div className="uap-dropdown-search">
            <div className="uap-dropdown-input-wrap">
              <Icon name="Search" size={13} style={{ color: '#9aa3af', flexShrink: 0 }} />
              <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search name or request ID…" className="uap-dropdown-input" />
              {search && (
                <button onClick={() => setSearch('')} style={{ color: '#9aa3af', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <Icon name="X" size={11} />
                </button>
              )}
            </div>
          </div>
          <div style={{ maxHeight: 210, overflowY: 'auto' }}>
            {filtered.length === 0
              ? <p style={{ textAlign: 'center', fontSize: 12, color: '#9aa3af', padding: '20px 0', margin: 0 }}>No users found</p>
              : filtered.map((opt) => (
                  <button key={opt.value} onClick={() => pick(opt)}
                    className={`uap-dropdown-row ${opt.value === value ? 'selected' : ''}`}
                    style={{ background: opt.value === value ? '#f0f1ff' : undefined }}>
                    <span style={{ width: 28, height: 28, borderRadius: '50%', background: avatarBg(opt.label), color: '#fff', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {getInitials(opt.label)}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: opt.value === value ? '#3730a3' : '#0d0d12', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {opt.label}
                      </p>
                      {opt.reqId && <p style={{ margin: 0, fontSize: 11, color: '#9aa3af', fontFamily: 'JetBrains Mono, monospace' }}>{opt.reqId}</p>}
                    </div>
                    <span className={`uap-badge ${opt.isPaid ? 'uap-badge-paid' : 'uap-badge-free'}`}>
                      {opt.isPaid ? 'Paid' : 'Free'}
                    </span>
                    {opt.value === value && <Icon name="Check" size={13} style={{ color: '#6366f1', flexShrink: 0 }} />}
                  </button>
                ))
            }
          </div>
          <div className="uap-dropdown-footer">
            {[['#10b981', 'Paid'], ['#cbd5e1', 'Free']].map(([c, l]) => (
              <span key={l} className="uap-legend">
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: c }} />{l}
              </span>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// ─── File Row ─────────────────────────────────────────────────────────────────

const FileRow = ({ file, isAssigned, assignment, userOptions, users, onUserSelect, onPreview, onDownload, onDelete, downloadingId }) => {
  const isPaid = ['paid', 'PAID'].includes(file.auditType || file.report_type || file.reportType || '');
  const fileName = file.name || file.file_name || file.storagePath?.split('/').pop() || 'Audit Report';
  const selectedUser = assignment ? users.find(u => u.id === assignment.userId) : null;
  const arrivalTime = file.arrivedAt || new Date().toISOString();

  const stateLabel  = isAssigned
    ? 'completed'
    : assignment
      ? 'queued'
      : 'pending';

  const accentColor = isAssigned ? '#10b981' : assignment ? '#6366f1' : '#e2e8f0';
  const iconBg      = isAssigned ? '#ecfdf5' : '#f4f6f8';
  const iconBorder  = isAssigned ? '#a7f3d0' : '#e8ecf0';

  const stateLabelDisplay = {
    completed: 'Completed',
    queued:    'Queued',
    pending:   'Pending',
  }[stateLabel] ?? stateLabel;

  return (
    <div className="uap-file-row">
      <div className="uap-file-row-accent" style={{ background: accentColor }} />
      <div className="uap-file-icon" style={{ background: iconBg, borderColor: iconBorder }}>
        <Icon
          name={isAssigned ? 'FileCheck' : 'FileCode2'}
          size={16}
          style={{ color: isAssigned ? '#059669' : '#64748b' }}
        />
      </div>

      {/* File info */}
      <div style={{ flex: 1, minWidth: 0, paddingTop: 2, paddingBottom: 2 }}>
        <p className="uap-filename" title={fileName}>{shortenFileName(fileName)}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
          {file.company_name && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '0.01em' }}>{file.company_name}</span>
          )}
          <span className={`uap-badge ${isPaid ? 'uap-badge-paid' : 'uap-badge-free'}`}>
            {isPaid ? '● Paid' : 'Free'}
          </span>
          <span className={`uap-badge uap-badge-${stateLabel}`}>
            {stateLabelDisplay}
          </span>
        </div>
      </div>

      {/* Right controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        {!isAssigned ? (
          <AssignSelect options={userOptions} value={assignment?.value || ''} onChange={(val) => onUserSelect(file.id, val)} />
        ) : (
          <div className="uap-delivered-chip">
            {selectedUser ? (
              <>
                <span style={{ width: 20, height: 20, borderRadius: '50%', background: avatarBg(selectedUser?.name || ''), color: '#fff', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {getInitials(selectedUser?.name || selectedUser?.email || '')}
                </span>
                <span>{(selectedUser?.name || selectedUser?.email?.split('@')[0] || '—').split(' ')[0]}</span>
              </>
            ) : (
              <Icon name="CheckCircle2" size={13} style={{ color: '#10b981' }} />
            )}
            <Icon name="CheckCircle2" size={12} style={{ color: '#10b981' }} />
          </div>
        )}

        <div style={{ textAlign: 'right', minWidth: 52 }}>
          <p className="uap-time-val">{formatRelativeTime(arrivalTime)}</p>
          <p className="uap-time-sub">arrived</p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <button className="uap-action-btn preview" onClick={() => onPreview(file)} title="Preview">
            <Icon name="Eye" size={14} />
          </button>
          <button className="uap-action-btn download" onClick={(e) => onDownload(file, e)} title="Download">
            <Icon name={downloadingId === file.id ? 'Loader2' : 'Download'} size={14} className={downloadingId === file.id ? 'animate-spin' : ''} />
          </button>
          <button className="uap-action-btn delete" onClick={(e) => onDelete(file, e)} title="Delete">
            <Icon name="Trash2" size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const UserAssignmentPanel = ({ htmlFiles = [], users = [], onAssignComplete, onDeleteFile, onAssignmentSuccess, supabaseClient }) => {
  useEffect(() => { injectStyles(); }, []);

  const [assignments,        setAssignments]        = useState({});
  const [isProcessing,       setIsProcessing]       = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [assignedFileIds,    setAssignedFileIds]    = useState(new Set());
  const [localFiles,         setLocalFiles]         = useState([]);
  const [showAssigned,       setShowAssigned]       = useState(false);

  // ── Mismatch warning state ────────────────────────────────────────────────
  const [mismatchPending, setMismatchPending] = useState(null);
  // mismatchPending = { fileId, value, userId, submissionId, fileAuditType, userAuditType, fileName, userName }

  // ── Preserve confirmed assignments for avatar chip in delivered section ───
  const confirmedAssignments = useRef({});

  // ── Persist confirmed IDs in a ref so they survive prop re-renders ────────
  const confirmedFileIds = useRef(new Set());

  const [previewFile,    setPreviewFile]    = useState(null);
  const [isPreviewOpen,  setIsPreviewOpen]  = useState(false);
  const [isLoadingHtml,  setIsLoadingHtml]  = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewError,   setPreviewError]   = useState(null);
  const cache      = useRef({});
  const blobUrlRef = useRef(null);

  const [fileToDelete,      setFileToDelete]      = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting,        setIsDeleting]        = useState(false);
  const [downloadingId,     setDownloadingId]     = useState(null);
  const [isDownloadingAll,  setIsDownloadingAll]  = useState(false);

  useEffect(() => {
    const sorted = [...htmlFiles]
      .map(normalizeFile)
      .filter(f => {
        const isHtml     = f.name?.endsWith('.html') || f.storagePath?.endsWith('.html') || f.type === 'html';
        const notJson    = !f.name?.endsWith('.json') && f.type !== 'json';
        const hasContent = f.html_content || f.storagePath || f.viewUrl || f.public_url;
        return isHtml && notJson && hasContent;
      })
      .sort((a, b) => new Date(b.arrivedAt) - new Date(a.arrivedAt));

    setLocalFiles(sorted);

    setAssignedFileIds(prev => {
      const next = new Set(prev);
      sorted.forEach(f => {
        if (confirmedFileIds.current.has(f.id)) { next.add(f.id); }
        const status = (f.status || f.assignmentStatus || f.assigned_status || f.delivery_status || '').toLowerCase();
        if (['assigned', 'completed', 'delivered'].includes(status)) { next.add(f.id); }
      });
      return next;
    });
  }, [htmlFiles]);

  const unassignedFiles = localFiles.filter(f => !assignedFileIds.has(f.id));
  const assignedFiles   = localFiles.filter(f => assignedFileIds.has(f.id));
  const selectedCount   = Object.keys(assignments).length;

  const userOptions = users.map(u => ({
    value: `${u.id}::${u.submissionId || u.id}`,
    label: u.name || u.email?.split('@')[0] || 'Unknown',
    reqId: u.requestId || null,
    isPaid: u.auditType === 'paid',
    auditType: u.auditType || 'free',
    name: u.name || u.email?.split('@')[0] || 'Unknown',
  }));

  const loadHtmlContent = async (file) => {
    if (cache.current[file.id]) return cache.current[file.id];
    const storagePath = file.full_path || file.storagePath || file.html_storage_path;
    if (supabaseClient && storagePath) {
      try {
        const { data, error } = await supabaseClient.storage.from('html-reports').download(storagePath);
        if (!error && data) { const t = await data.text(); cache.current[file.id] = t; return t; }
      } catch {}
      try {
        const { data: sd } = await supabaseClient.storage.from('html-reports').createSignedUrl(storagePath, 60);
        if (sd?.signedUrl) { const r = await fetch(sd.signedUrl); if (r.ok) { const t = await r.text(); cache.current[file.id] = t; return t; } }
      } catch {}
    }
    const url = file.viewUrl || file.public_url;
    if (url) {
      try {
        const r = await fetch(url, { cache: 'no-store' });
        if (r.ok) { const t = await r.text(); if (t.includes('<!doctype html') && t.length > 5000) { cache.current[file.id] = t; return t; } }
      } catch {}
    }
    if (file.html_content) { cache.current[file.id] = file.html_content; return file.html_content; }
    return null;
  };

  const handlePreviewFile = async (file) => {
    setPreviewFile(file); setPreviewBlobUrl(null); setPreviewError(null);
    setIsPreviewOpen(true); setIsLoadingHtml(true);
    if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
    try {
      const html = await loadHtmlContent(file);
      if (html) {
        const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url; setPreviewBlobUrl(url);
      } else setPreviewError('Could not load HTML content.');
    } catch (err) { setPreviewError('Failed to load preview: ' + err.message); }
    finally { setIsLoadingHtml(false); }
  };

  const closePreview = () => {
    setIsPreviewOpen(false); setPreviewFile(null); setPreviewBlobUrl(null); setPreviewError(null);
    if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
  };

  const triggerDownload = (html, fileName) => {
    const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = fileName;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleDownloadFile = async (file, e) => {
    e?.stopPropagation(); setDownloadingId(file.id);
    try {
      const html = await loadHtmlContent(file);
      if (!html) throw new Error('Could not load file content.');
      const name = file.name || file.file_name || file.storagePath?.split('/').pop() || `audit_${file.id}.html`;
      triggerDownload(html, name); toast.success('Downloaded', { icon: '📥' });
    } catch (err) { toast.error('Download failed: ' + err.message); }
    finally { setDownloadingId(null); }
  };

  const handleDownloadAll = async () => {
    if (!unassignedFiles.length) return;
    setIsDownloadingAll(true); let ok = 0, fail = 0;
    toast.loading(`Downloading ${unassignedFiles.length} files…`, { id: 'dl-all' });
    for (let i = 0; i < unassignedFiles.length; i++) {
      const file = unassignedFiles[i];
      try {
        const html = await loadHtmlContent(file);
        if (!html) { fail++; continue; }
        const name = file.name || file.file_name || file.storagePath?.split('/').pop() || `audit_${file.id}.html`;
        triggerDownload(html, name); ok++;
        if (i < unassignedFiles.length - 1) await new Promise(r => setTimeout(r, 600));
      } catch { fail++; }
    }
    toast.dismiss('dl-all');
    fail === 0 ? toast.success(`Downloaded ${ok} files`) : toast(`Downloaded ${ok}, failed ${fail}`);
    setIsDownloadingAll(false);
  };

  const handleDeleteClick = (file, e) => { e.stopPropagation(); setFileToDelete(file); setIsDeleteModalOpen(true); };

  const handleConfirmDelete = async () => {
    if (!fileToDelete) return; setIsDeleting(true);
    try {
      if (onDeleteFile) await onDeleteFile(fileToDelete.id);
      else if (supabaseClient && fileToDelete.storagePath) {
        const { error } = await supabaseClient.storage.from('html-reports').remove([fileToDelete.storagePath]);
        if (error) throw new Error(error.message);
      } else throw new Error('No delete handler.');
      setLocalFiles(prev => prev.filter(f => f.id !== fileToDelete.id));
      setAssignments(prev => { const n = { ...prev }; delete n[fileToDelete.id]; return n; });
      setAssignedFileIds(prev => { const n = new Set(prev); n.delete(fileToDelete.id); return n; });
      confirmedFileIds.current.delete(fileToDelete.id);
      delete cache.current[fileToDelete.id];
      delete confirmedAssignments.current[fileToDelete.id];
      setIsDeleteModalOpen(false); setFileToDelete(null);
      toast.success('File deleted');
    } catch (err) { toast.error('Delete failed: ' + err.message); }
    finally { setIsDeleting(false); }
  };

  // ─── handleUserSelect — with mismatch check ───────────────────────────────
  const handleUserSelect = useCallback((fileId, value) => {
    // Clear selection
    if (!value) {
      setAssignments(prev => { const n = { ...prev }; delete n[fileId]; return n; });
      return;
    }

    const [userId, submissionId] = value.split('::');

    // Find the file and the selected user option to compare audit types
    const file       = localFiles.find(f => f.id === fileId);
    const userOption = userOptions.find(o => o.value === value);

    const fileType = (file?.auditType || '').toLowerCase();        // 'paid' | 'free'
    const userType = (userOption?.auditType || '').toLowerCase();  // 'paid' | 'free'

    // Mismatch detected — show warning modal instead of immediately assigning
    if (file && userOption && fileType !== userType) {
      setMismatchPending({
        fileId,
        value,
        userId,
        submissionId,
        fileAuditType: fileType,
        userAuditType: userType,
        fileName:  file.name || file.file_name || '',
        userName:  userOption.name || userOption.label || '',
      });
      return; // Don't assign yet — wait for user decision
    }

    // No mismatch — assign directly
    setAssignments(prev => ({ ...prev, [fileId]: { userId, submissionId, value } }));
  }, [localFiles, userOptions]);

  // ─── Mismatch modal handlers ──────────────────────────────────────────────

  // Admin clicks "Cancel" — dismiss warning, don't assign
  const handleMismatchCancel = useCallback(() => {
    setMismatchPending(null);
  }, []);

  // Admin clicks "Assign Anyway" — proceed despite mismatch
  const handleMismatchOverride = useCallback(() => {
    if (!mismatchPending) return;
    const { fileId, value, userId, submissionId, fileAuditType, userAuditType, userName } = mismatchPending;

    // Log override for audit trail visibility
    console.warn(
      `[UAP] Audit type mismatch OVERRIDE — file: ${fileAuditType.toUpperCase()}, user requested: ${userAuditType.toUpperCase()}, user: ${userName}`
    );

    toast(`⚠️ Mismatch override: assigning ${fileAuditType.toUpperCase()} report to ${userAuditType.toUpperCase()} request`, {
      duration: 5000,
      style: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' },
    });

    setAssignments(prev => ({ ...prev, [fileId]: { userId, submissionId, value } }));
    setMismatchPending(null);
  }, [mismatchPending]);

  const handleConfirmAssignment = async (assignmentDetails) => {
    setIsProcessing(true);
    try {
      const data = assignmentDetails.map(d => {
        const file = unassignedFiles.find(f => f.id === d.fileId);
        const sp = file?.full_path || file?.storagePath;
        const auditId = file?.auditId || (file?.id?.startsWith('audit-') ? file.id.replace('audit-', '') : null);
        return {
          ...d, auditId,
          auditType:   file?.auditType || 'free',
          assignedAt:  new Date().toISOString(),
          htmlContent: file?.html_content,
          htmlUrl:     file?.viewUrl || file?.public_url,
          storagePath: sp, fullPath: sp,
        };
      });

      const ids = data.map(f => f.fileId);

      ids.forEach(fileId => {
        if (assignments[fileId]) {
          confirmedAssignments.current[fileId] = assignments[fileId];
        }
      });

      ids.forEach(id => confirmedFileIds.current.add(id));

      setAssignedFileIds(prev => { const n = new Set(prev); ids.forEach(id => n.add(id)); return n; });
      setAssignments({});
      setIsConfirmModalOpen(false);
      setShowAssigned(true);

      await onAssignComplete(data);

      if (onAssignmentSuccess) onAssignmentSuccess(ids);
    } catch (err) {
      console.error('Assignment error:', err);
      toast.error('Assignment failed.');
    } finally {
      setIsProcessing(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="uap-root">
      <div className="uap-panel">

        {/* ── Header ── */}
        <div className="uap-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="uap-header-icon">
              <Icon name="Send" size={15} style={{ color: '#fff' }} />
            </div>
            <div>
              <p className="uap-title">HTML Audits Assign Console</p>
              <p className="uap-subtitle">
                {localFiles.length === 0
                  ? 'No reports yet'
                  : `${unassignedFiles.length} pending · ${assignedFiles.length} delivered`}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 6, marginLeft: 4, flexWrap: 'wrap' }}>
              {unassignedFiles.length > 0 && (
                <span className="uap-pill uap-pill-amber">
                  <span className="uap-pill-dot" style={{ background: '#f59e0b' }} />
                  {unassignedFiles.length} unassigned
                </span>
              )}
              {selectedCount > 0 && (
                <span className="uap-pill uap-pill-indigo">
                  <span className="uap-pill-dot" style={{ background: '#6366f1' }} />
                  {selectedCount} queued
                </span>
              )}
              {assignedFiles.length > 0 && (
                <span className="uap-pill uap-pill-green">
                  <span className="uap-pill-dot" style={{ background: '#10b981' }} />
                  {assignedFiles.length} delivered
                </span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            {unassignedFiles.length > 0 && (
              <button className="uap-dl-btn" onClick={handleDownloadAll} disabled={isDownloadingAll}>
                <Icon name={isDownloadingAll ? 'Loader2' : 'DownloadCloud'} size={13} className={isDownloadingAll ? 'animate-spin' : ''} />
                Download all
              </button>
            )}
            {selectedCount > 0 && (
              <button className="uap-assign-all-btn" onClick={() => setIsConfirmModalOpen(true)} disabled={isProcessing}>
                {isProcessing
                  ? <><Icon name="Loader2" size={13} className="animate-spin" /> Assigning…</>
                  : <><Icon name="Send" size={13} /> Assign {selectedCount}</>
                }
              </button>
            )}
          </div>
        </div>

        {/* ── Column headers ── */}
        {localFiles.length > 0 && (
          <div className="uap-col-header">
            <div style={{ width: 3, marginRight: 12, flexShrink: 0 }} />
            <div style={{ width: 38, marginRight: 12, flexShrink: 0 }} />
            <span className="uap-col-label" style={{ flex: 1 }}>File</span>
            <span className="uap-col-label" style={{ width: 140, textAlign: 'right' }}>Assignee</span>
            <span className="uap-col-label" style={{ width: 72, textAlign: 'right' }}>Arrived</span>
            <span className="uap-col-label" style={{ width: 100, textAlign: 'right' }}>Actions</span>
          </div>
        )}

        {/* ── No users warning ── */}
        {users.length === 0 && unassignedFiles.length > 0 && (
          <div className="uap-warn">
            <Icon name="AlertTriangle" size={14} style={{ color: '#d97706', flexShrink: 0 }} />
            <p style={{ margin: 0, fontSize: 12, color: '#92400e', fontWeight: 500 }}>
              No users available — users need pending submissions to appear here
            </p>
          </div>
        )}

        {/* ── Empty state ── */}
        {localFiles.length === 0 && (
          <div className="uap-empty">
            <div className="uap-empty-icon">
              <Icon name="FileCode2" size={22} style={{ color: '#9aa3af' }} />
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#334155' }}>No audit files yet</p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9aa3af' }}>Upload JSON files to generate HTML reports</p>
            </div>
          </div>
        )}

        {/* ── Unassigned rows ── */}
        {unassignedFiles.length > 0 && (
          <div>
            {unassignedFiles.map(file => (
              <FileRow
                key={file.id}
                file={file}
                isAssigned={false}
                assignment={assignments[file.id]}
                userOptions={userOptions}
                users={users}
                onUserSelect={handleUserSelect}
                onPreview={handlePreviewFile}
                onDownload={handleDownloadFile}
                onDelete={handleDeleteClick}
                downloadingId={downloadingId}
              />
            ))}
          </div>
        )}

        {/* ── Delivered collapsible ── */}
        {assignedFiles.length > 0 && (
          <div style={{ borderTop: '1px solid #f0f2f5' }}>
            <button className="uap-collapse-btn" onClick={() => setShowAssigned(v => !v)}>
              <Icon name={showAssigned ? 'ChevronDown' : 'ChevronRight'} size={13} style={{ color: '#64748b' }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Delivered</span>
              <span style={{ fontSize: 11, color: '#9aa3af', fontFamily: 'JetBrains Mono, monospace', background: '#f4f6f8', padding: '1px 7px', borderRadius: 10, border: '1px solid #e8ecf0' }}>
                {assignedFiles.length}
              </span>
            </button>
            {showAssigned && (
              <div style={{ borderTop: '1px solid #f0f2f5' }}>
                {assignedFiles.map(file => (
                  <FileRow
                    key={file.id}
                    file={file}
                    isAssigned={true}
                    assignment={confirmedAssignments.current[file.id] ?? null}
                    userOptions={userOptions}
                    users={users}
                    onUserSelect={handleUserSelect}
                    onPreview={handlePreviewFile}
                    onDownload={handleDownloadFile}
                    onDelete={handleDeleteClick}
                    downloadingId={downloadingId}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── All done banner ── */}
        {unassignedFiles.length === 0 && assignedFiles.length > 0 && (
          <div className="uap-done-banner">
            <Icon name="CheckCircle2" size={15} style={{ color: '#10b981' }} />
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#166534' }}>
              All {assignedFiles.length} audit{assignedFiles.length > 1 ? 's' : ''} delivered
            </p>
          </div>
        )}
      </div>

      {/* ── Audit Type Mismatch Modal ── */}
      <AuditTypeMismatchModal
        isOpen={!!mismatchPending}
        onClose={handleMismatchCancel}
        onOverride={handleMismatchOverride}
        fileAuditType={mismatchPending?.fileAuditType}
        userAuditType={mismatchPending?.userAuditType}
        fileName={mismatchPending?.fileName}
        userName={mismatchPending?.userName}
      />

      {/* ── Confirmation Modal ── */}
      <AssignmentConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmAssignment}
        assignments={assignments}
        uploadedFiles={unassignedFiles}
        users={users}
        isProcessing={isProcessing}
      />

      {/* ── Preview Modal ── */}
      {isPreviewOpen && (
        <>
          <div className="uap-backdrop" onClick={closePreview} />
          <div className="uap-preview-shell">
            <div className="uap-preview-bar">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: '#f4f6f8', border: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name="FileCode2" size={14} style={{ color: '#64748b' }} />
                </div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#0d0d12', fontFamily: 'JetBrains Mono, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {shortenFileName(previewFile?.name || previewFile?.storagePath?.split('/').pop() || 'Preview')}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, marginLeft: 16, flexShrink: 0 }}>
                <button onClick={e => previewFile && handleDownloadFile(previewFile, e)} disabled={downloadingId === previewFile?.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 12, fontWeight: 600, color: '#334155', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                  <Icon name="Download" size={13} /> Download
                </button>
                <button onClick={closePreview}
                  style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' }}>
                  <Icon name="X" size={15} />
                </button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'hidden', background: '#f8f9fb', position: 'relative' }}>
              {isLoadingHtml && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#fff', gap: 8 }}>
                  <Icon name="Loader2" size={24} className="animate-spin" style={{ color: '#9aa3af' }} />
                  <p style={{ fontSize: 12, color: '#9aa3af', margin: 0 }}>Loading report…</p>
                </div>
              )}
              {!isLoadingHtml && previewError && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#fff', gap: 8, padding: 32, textAlign: 'center' }}>
                  <Icon name="AlertTriangle" size={24} style={{ color: '#d1d5db' }} />
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#64748b', margin: 0 }}>Preview unavailable</p>
                  <p style={{ fontSize: 12, color: '#9aa3af', margin: 0, maxWidth: 360 }}>{previewError}</p>
                </div>
              )}
              {!isLoadingHtml && !previewError && previewBlobUrl && (
                <iframe key={previewBlobUrl} src={previewBlobUrl} title="Preview" style={{ width: '100%', height: '100%', border: 'none', display: 'block' }} />
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Delete Modal ── */}
      {isDeleteModalOpen && fileToDelete && (
        <div className="uap-delete-modal" style={{ background: 'rgba(13,13,18,0.5)', backdropFilter: 'blur(6px)' }} onClick={() => !isDeleting && setIsDeleteModalOpen(false)}>
          <div className="uap-delete-card uap-fadein" onClick={e => e.stopPropagation()}>
            <div style={{ height: 2, background: 'linear-gradient(90deg, #ef4444, #f87171)' }} />
            <div style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name="Trash2" size={16} style={{ color: '#dc2626' }} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0d0d12', fontFamily: 'DM Sans, sans-serif' }}>Delete file?</p>
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: '#9aa3af', fontFamily: 'DM Sans, sans-serif' }}>This action cannot be undone.</p>
                </div>
              </div>
              <div style={{ padding: '10px 12px', background: '#f8f9fb', borderRadius: 8, border: '1px solid #e8ecf0', marginBottom: 18 }}>
                <p style={{ margin: 0, fontSize: 12, fontFamily: 'JetBrains Mono, monospace', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                  {shortenFileName(fileToDelete.name || fileToDelete.storagePath?.split('/').pop())}
                </p>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => { setIsDeleteModalOpen(false); setFileToDelete(null); }} disabled={isDeleting}
                  style={{ height: 36, padding: '0 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                  Cancel
                </button>
                <button onClick={handleConfirmDelete} disabled={isDeleting}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 16px', borderRadius: 8, border: 'none', background: '#dc2626', fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer', opacity: isDeleting ? 0.6 : 1, fontFamily: 'DM Sans, sans-serif' }}>
                  {isDeleting ? <><Icon name="Loader2" size={13} className="animate-spin" /> Deleting…</> : <><Icon name="Trash2" size={13} /> Delete</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAssignmentPanel;